// Colors and type follow the notifications panel and the app's teal actions
// (GradientButton #46A091, links #248B70, text #2F2B3D, borders #EAEAEA).

const TEAL = "#46A091";
const TEAL_DARK = "#248B70";
const TEXT = "#2F2B3D";
const BORDER = "rgba(234, 234, 234, 1)";
const SOFT = "#F8F7FA";
const RED = "#d32f2f";
const MUTED = "rgba(47, 43, 61, 0.6)";

/** A write's card border: teal while it waits for the person, neutral once answered. */
const CARD_BORDER = { proposed: TEAL, executed: "rgba(70, 160, 145, 0.45)", failed: "rgba(211, 47, 47, 0.35)" };
const FEED_STATUS = {
  executed: { color: TEAL_DARK, background: "rgba(70, 160, 145, 0.12)" },
  undone: { color: MUTED, background: SOFT },
  declined: { color: MUTED, background: SOFT },
  expired: { color: MUTED, background: SOFT },
  failed: { color: RED, background: "rgba(211, 47, 47, 0.08)" },
};

export const agentChatStyles = {
  launcher: (isBelowMd) => ({
    position: "fixed",
    bottom: isBelowMd ? "16px" : "24px",
    insetInlineEnd: isBelowMd ? "16px" : "24px",
    zIndex: 1200,
    color: "#fff",
    background: `linear-gradient(to right, ${TEAL} 0%, ${TEAL}B3 100%)`,
    boxShadow: "0px 2px 6px 0px #7367F04D",
    "&:hover": { background: TEAL },
  }),
  panel: (isBelowMd) => ({
    width: isBelowMd ? "100%" : "420px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderInlineStart: isBelowMd ? "none" : `1px solid ${BORDER}`,
    boxShadow: isBelowMd ? "none" : "0px 4px 18px rgba(47, 43, 61, 0.1)",
  }),
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    px: "16px",
    py: "14px",
    borderBottom: `1px solid ${BORDER}`,
  },
  headerIcon: { color: TEAL, fontSize: "20px" },
  title: { fontSize: "15px", fontWeight: 500, color: "rgba(72, 72, 72, 1)", lineHeight: "22px" },
  list: {
    flex: 1,
    overflowY: "auto",
    px: "16px",
    py: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  empty: { display: "flex", flexDirection: "column", gap: "8px", mt: "8px" },
  greeting: { fontSize: "16px", fontWeight: 500, color: TEXT },
  intro: { fontSize: "13px", color: "rgba(47, 43, 61, 0.7)", lineHeight: "20px" },
  suggestions: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px", mt: "8px" },
  suggestion: {
    textTransform: "none",
    borderRadius: "8px",
    borderColor: BORDER,
    color: TEXT,
    fontSize: "13px",
    textAlign: "start",
    "&:hover": { borderColor: TEAL, background: "rgba(70, 160, 145, 0.06)" },
  },
  userBubble: {
    alignSelf: "flex-end",
    maxWidth: "85%",
    background: "rgba(70, 160, 145, 0.12)",
    color: TEXT,
    borderRadius: "12px",
    px: "12px",
    py: "8px",
    fontSize: "14px",
    lineHeight: "21px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  assistantRow: { alignSelf: "flex-start", maxWidth: "92%", display: "flex", flexDirection: "column", gap: "6px" },
  assistantBubble: {
    background: SOFT,
    borderRadius: "12px",
    px: "12px",
    py: "8px",
    color: TEXT,
    wordBreak: "break-word",
  },
  textBlock: { fontSize: "14px", lineHeight: "21px", color: TEXT, "& + &": { mt: "8px" } },
  textList: { m: 0, my: "4px", paddingInlineStart: "20px", fontSize: "14px", lineHeight: "21px", color: TEXT },
  textListLine: { paddingInlineStart: "4px" },
  chips: { display: "flex", flexWrap: "wrap", gap: "6px" },
  chip: {
    height: "24px",
    fontSize: "12px",
    color: "rgba(47, 43, 61, 0.7)",
    borderColor: BORDER,
    background: "#fff",
    "& .MuiChip-icon": { fontSize: "14px", color: TEAL },
  },
  thinking: { display: "flex", alignItems: "center", gap: "8px", px: "4px", py: "6px" },
  thinkingDots: { display: "inline-flex", alignItems: "center", gap: "3px" },
  /** One of the three dots; each starts a beat after the one before. Still under reduced motion. */
  thinkingDot: (index) => ({
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: TEAL,
    animation: `agentThinking 1.2s ease-in-out ${index * 0.16}s infinite`,
    "@keyframes agentThinking": {
      "0%, 80%, 100%": { transform: "translateY(0)", opacity: 0.4 },
      "40%": { transform: "translateY(-3px)", opacity: 1 },
    },
    "@media (prefers-reduced-motion: reduce)": { animation: "none", opacity: 0.6 },
  }),
  thinkingText: {
    fontSize: "13px",
    color: MUTED,
    animation: "agentThinkingPulse 2s ease-in-out infinite",
    "@keyframes agentThinkingPulse": { "0%, 100%": { opacity: 1 }, "50%": { opacity: 0.55 } },
    "@media (prefers-reduced-motion: reduce)": { animation: "none" },
  },
  buttons: { display: "flex", flexWrap: "wrap", gap: "8px" },
  feedback: { display: "flex", gap: "2px", mt: "-2px" },
  feedbackButton: (selected) => ({ p: "4px", color: selected ? TEAL : "rgba(47, 43, 61, 0.4)" }),
  handoff: {
    textTransform: "none",
    borderRadius: "8px",
    borderColor: TEAL,
    color: TEAL_DARK,
    fontSize: "13px",
    "&:hover": { borderColor: TEAL_DARK, background: "rgba(70, 160, 145, 0.06)" },
  },
  openIcon: { fontSize: "16px !important", "[dir=rtl] &": { transform: "scaleX(-1)" } },
  error: {
    border: "1px solid rgba(211, 47, 47, 0.3)",
    background: "rgba(211, 47, 47, 0.05)",
    borderRadius: "8px",
    px: "12px",
    py: "10px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  errorText: { fontSize: "13px", color: "#d32f2f", flex: 1 },
  retry: { textTransform: "none", color: TEAL_DARK, fontSize: "13px", flexShrink: 0 },
  composer: {
    display: "flex",
    alignItems: "flex-end",
    gap: "8px",
    px: "16px",
    pt: "12px",
    borderTop: `1px solid ${BORDER}`,
  },
  input: {
    "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: "14px" },
    "& .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: TEAL },
  },
  sendButton: { color: TEAL, mb: "2px" },
  // ── writes: the Confirm card, receipts, Undo (COMY1-391) ──
  card: (status) => ({
    border: `1px solid ${CARD_BORDER[status] ?? BORDER}`,
    borderRadius: "12px",
    background: "#fff",
    px: "12px",
    py: "10px",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    width: "100%",
    boxSizing: "border-box",
  }),
  cardTitle: { fontSize: "14px", fontWeight: 600, color: TEXT, lineHeight: "20px" },
  cardFields: { m: 0, display: "flex", flexDirection: "column", gap: "4px" },
  cardField: { display: "grid", gridTemplateColumns: "minmax(84px, 36%) 1fr", gap: "8px", alignItems: "baseline" },
  cardLabel: { m: 0, fontSize: "12px", color: MUTED, lineHeight: "18px" },
  // a value can be a whole text (the community's terms, S5): its lines as written, scrolled when long
  cardValue: { m: 0, fontSize: "13px", color: TEXT, lineHeight: "18px", wordBreak: "break-word", whiteSpace: "pre-wrap", maxHeight: "320px", overflowY: "auto" },
  cardOutcome: { display: "flex", alignItems: "flex-start", gap: "6px" },
  outcomeIcon: (kind) => ({ fontSize: "18px", mt: "1px", color: kind === "done" ? TEAL : kind === "failed" ? RED : MUTED }),
  receipt: { fontSize: "13px", color: TEXT, lineHeight: "19px" },
  cardNote: { fontSize: "12px", color: MUTED, lineHeight: "18px" },
  cardButtons: { display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" },
  confirm: {
    textTransform: "none",
    borderRadius: "8px",
    fontSize: "13px",
    minWidth: "88px",
    background: `linear-gradient(to right, ${TEAL} 0%, ${TEAL}B3 100%)`,
    "&:hover": { background: TEAL },
  },
  edit: { textTransform: "none", borderRadius: "8px", fontSize: "13px", color: TEAL_DARK },
  cancel: { textTransform: "none", borderRadius: "8px", fontSize: "13px", color: TEXT, borderColor: BORDER },
  // ── "What I did" ──
  viewToggle: (active) => ({ color: active ? TEAL : "rgba(47, 43, 61, 0.7)" }),
  feed: { listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: "10px" },
  feedItem: { border: `1px solid ${BORDER}`, borderRadius: "10px", px: "12px", py: "10px", display: "flex", flexDirection: "column", gap: "4px" },
  feedHead: { display: "flex", alignItems: "center", gap: "8px" },
  feedStatus: (status) => ({ fontSize: "11px", fontWeight: 500, borderRadius: "6px", px: "6px", py: "1px", ...(FEED_STATUS[status] ?? FEED_STATUS.expired) }),
  feedTime: { fontSize: "11px", color: MUTED, marginInlineStart: "auto" },
  feedText: { fontSize: "13px", color: TEXT, lineHeight: "19px" },
  feedNote: { fontSize: "12px", color: RED, lineHeight: "18px" },
  feedLink: { alignSelf: "flex-start", textTransform: "none", fontSize: "12px", color: TEAL_DARK, px: "4px", minWidth: 0 },
  feedEmpty: { fontSize: "13px", color: MUTED, textAlign: "center", mt: "24px" },
  disclaimer: { fontSize: "11px", color: "rgba(47, 43, 61, 0.5)", px: "16px", pt: "6px", pb: "12px" },
  // ── files picked in the chat (COMY1-392) ──
  userRow: { alignSelf: "flex-end", maxWidth: "85%", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px" },
  attachments: { display: "flex", flexWrap: "wrap", gap: "6px" },
  attachment: (size) => ({
    position: "relative",
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: "8px",
    overflow: "hidden",
    border: `1px solid ${BORDER}`,
    background: SOFT,
    flexShrink: 0,
  }),
  attachmentImage: { width: "100%", height: "100%", objectFit: "cover", display: "block" },
  attachmentFile: { width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", color: RED, px: "2px" },
  attachmentName: { fontSize: "9px", color: MUTED, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  attachmentBusy: {
    position: "absolute",
    inset: 0,
    background: "rgba(255, 255, 255, 0.6)",
    "&::after": {
      content: '""',
      position: "absolute",
      insetInlineStart: 0,
      bottom: 0,
      height: "3px",
      width: "100%",
      background: `linear-gradient(90deg, transparent, ${TEAL}, transparent)`,
      animation: "agentUpload 1.2s linear infinite",
    },
    "@keyframes agentUpload": { "0%": { transform: "translateX(-100%)" }, "100%": { transform: "translateX(100%)" } },
  },
  attachmentRemove: {
    position: "absolute",
    top: "2px",
    insetInlineEnd: "2px",
    p: "2px",
    background: "rgba(255, 255, 255, 0.9)",
    "&:hover": { background: "#fff" },
  },
  pending: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", px: "16px", pt: "10px", borderTop: `1px solid ${BORDER}` },
  fileError: { fontSize: "12px", color: RED, flexBasis: "100%" },
  attachPrivacy: { fontSize: "11px", color: MUTED, lineHeight: "16px", flexBasis: "100%" },
  attachButton: { color: "rgba(47, 43, 61, 0.6)", mb: "2px", "&:hover": { color: TEAL } },
  // ── a pricelist or product change: old → new (COMY1-392) ──
  diff: { width: "100%", borderCollapse: "collapse", fontSize: "13px", color: TEXT },
  visuallyHidden: { position: "absolute", width: "1px", height: "1px", overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" },
  diffRow: { borderTop: `1px solid ${BORDER}`, "&:first-of-type": { borderTop: "none" } },
  diffLabel: { textAlign: "start", fontWeight: 400, py: "5px", paddingInlineEnd: "8px", lineHeight: "18px", verticalAlign: "top" },
  diffDetail: { display: "block", fontSize: "11px", color: MUTED, lineHeight: "16px" },
  diffFlag: { display: "flex", alignItems: "center", gap: "3px", fontSize: "11px", color: "#b26a00", lineHeight: "16px", mt: "2px" },
  diffFrom: { py: "5px", color: MUTED, textDecoration: "line-through", whiteSpace: "nowrap", verticalAlign: "top", textAlign: "end" },
  diffArrow: { py: "5px", px: "4px", color: MUTED, verticalAlign: "top" },
  diffTo: (removed) => ({ py: "5px", fontWeight: 600, whiteSpace: "nowrap", verticalAlign: "top", color: removed ? RED : TEAL_DARK }),
  notes: { display: "flex", flexDirection: "column", gap: "6px" },
  note: (key) => ({
    borderRadius: "8px",
    px: "10px",
    py: "8px",
    background: key.startsWith("activeOrders") ? "rgba(255, 159, 67, 0.12)" : key === "unsureLines" ? "rgba(211, 47, 47, 0.06)" : SOFT,
    borderInlineStart: `3px solid ${key.startsWith("activeOrders") ? "#ff9f43" : key === "unsureLines" ? RED : BORDER}`,
  }),
  noteTitle: { fontSize: "12px", fontWeight: 600, color: TEXT, lineHeight: "18px" },
  noteText: { fontSize: "12px", color: TEXT, lineHeight: "18px" },
  noteItems: { m: 0, mt: "4px", paddingInlineStart: "18px", fontSize: "12px", color: TEXT, lineHeight: "18px" },
  // ── saved chats (COMY1-392) ──
  historyItem: (current) => ({
    border: `1px solid ${current ? TEAL : BORDER}`,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    paddingInlineEnd: "6px",
  }),
  historyOpen: {
    flex: 1,
    minWidth: 0,
    textTransform: "none",
    justifyContent: "flex-start",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "2px",
    px: "12px",
    py: "8px",
    color: TEXT,
    textAlign: "start",
  },
  historyTitle: { fontSize: "13px", lineHeight: "19px", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  historyConfirm: { textTransform: "none", fontSize: "12px", flexShrink: 0 },
  historyNote: { fontSize: "11px", color: MUTED, lineHeight: "16px" },
  historyDeleteAll: { alignSelf: "flex-start", textTransform: "none", fontSize: "12px" },
};
